import { GraphQLResult, generateClient } from "aws-amplify/api";
import { FieldContinuationData, FieldContinuationDataForResolver } from "db/definitions";
import { bulkGetFieldContinuationDatasByParentFormTemplateId, createFieldContinuationDataForResolver } from "db/queries/fieldContinuationDataQueries";
import { bulkRetrieveFieldTemplatesByFormTemplate } from "db/queries/fieldTemplateQueries";
// @ts-ignore
import { getFieldContinuationDataByParentFormTemplateId } from "../graphql/queries";
import { recordSentry } from "utils/recordSentry";

interface QueryResponse {
	fieldContinuationDataByParentFormTemplateId: {
		items: FieldContinuationData[];
		nextToken?: string;
	};
}

/**
 * Given the formTemplate id and whether or not we're online, retrieves the fieldTemplates and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formTemplate id we want to fetch the fieldTemplates by
 * @param online Whether or not we're online
 * @returns The array of fieldTemplates retrieved
 */
async function batchGetFieldContinuationDataByFormTemplate(id: string, online: boolean): Promise<FieldContinuationDataForResolver[]> {
	if (id) {
		try {
			if (online) {
				let token = undefined;
				const fieldContDatas: FieldContinuationDataForResolver[] = [];
				while (token !== null) {
					const client = generateClient({ authMode: "userPool" });
					const result = (await client.graphql({
						query: getFieldContinuationDataByParentFormTemplateId,
						authMode: "userPool",
						variables: {
							parentFormTemplateId: id,
							nextToken: token,
						},
					})) as GraphQLResult<QueryResponse>;
					const fieldContinuationsFromResponse = result.data.fieldContinuationDataByParentFormTemplateId.items;

					for (const fieldContinuation of fieldContinuationsFromResponse) {
						const parsedContinuation = createFieldContinuationDataForResolver(fieldContinuation);
						fieldContDatas.push(parsedContinuation);
					}
					token = result.data.fieldContinuationDataByParentFormTemplateId.nextToken || null;
				}
				return fieldContDatas;
			} else {
				const fieldTemplates = await bulkRetrieveFieldTemplatesByFormTemplate(id);
				const contIds: string[] = [];
				for (const fieldTemplate of fieldTemplates) {
					if (fieldTemplate.continuationDataId) {
						contIds.push(fieldTemplate.continuationDataId);
					}
				}
				const fieldContDatas = await bulkGetFieldContinuationDatasByParentFormTemplateId(id);
				return fieldContDatas;
			}
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FIELD TEMPLATE ${online ? "ONLINE" : "OFFLINE"}` });
			return [];
		}
	} else {
		return [];
	}
}

export default batchGetFieldContinuationDataByFormTemplate;
